
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { CampaignFormValues } from "../CampaignForm";
import { format } from "date-fns";

interface VoucherPreviewDialogProps {
  formValues: CampaignFormValues;
  imagePreview: string | null;
}

export function VoucherPreviewDialog({ formValues, imagePreview }: VoucherPreviewDialogProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline">
          <Eye className="mr-2" />
          Preview Voucher
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Voucher Preview</DialogTitle>
          <DialogDescription>
            This is how your voucher will appear to users
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 border rounded-lg bg-card">
          <div className="flex flex-col gap-4">
            {/* Campaign title */}
            <h3 className="text-lg font-semibold text-center">{formValues.title || "Campaign Title"}</h3>
            
            {/* Voucher image */}
            {imagePreview ? (
              <div className="relative mx-auto">
                <img
                  src={imagePreview}
                  alt="Voucher preview"
                  className="max-h-32 object-contain rounded-md"
                />
              </div>
            ) : (
              <div className="w-full h-32 bg-muted rounded-md flex items-center justify-center">
                <p className="text-sm text-muted-foreground">No image uploaded</p>
              </div>
            )}
            
            {/* Voucher details */}
            <div className="space-y-2">
              <div className="text-center">
                <p className="text-sm font-medium">Valid from</p>
                <p className="text-xs text-muted-foreground">
                  {formValues.startDate ? format(formValues.startDate, 'MMM d, yyyy') : "Start date"} - {formValues.endDate ? format(formValues.endDate, 'MMM d, yyyy') : "End date"}
                </p>
              </div>
              
              <div className="bg-muted p-3 rounded-md">
                <p className="text-sm leading-snug">{formValues.voucherDetails || "Voucher details will appear here"}</p>
              </div>
              
              {formValues.sponsorName && (
                <div className="text-center pt-2">
                  <p className="text-xs font-medium">Sponsored by</p>
                  <p className="text-sm">{formValues.sponsorName}</p>
                </div>
              )}
              
              {formValues.sponsorMessage && (
                <p className="text-xs italic text-center">{formValues.sponsorMessage}</p>
              )}
            </div>
            
            {/* Voucher code placeholder */}
            <div className="bg-primary/10 p-3 rounded-md border text-center">
              <p className="text-xs text-muted-foreground">Voucher code</p>
              <p className="font-mono font-bold tracking-wider text-primary">
                {formValues.campaignType === "unique" ? "XXXX-XXXX-XXXX" : "SAMPCODE"}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
